import produce from 'immer';
import { handleActions } from 'redux-actions';
import { isNil } from 'lodash';
import { EVENT_SAVE_RESULT } from '../../../constants/service/app/eventConstants';
import actions from './actions';
import { _LIST_PAGINATION_MODEL, LIST_PAGINATION_MODEL, LIST_SUMMARY_MODEL } from '../app/models';
import {
  EVENT_LIST_FILTER_OPTION_MODEL,
  EVENT_INFO_MODEL,
  EVENT_SECTION_INFO_MODEL,
  EVENT_PRODUCT_LIST_FILTER_OPTION_MODEL,
} from './models';
import { TAG_LINK_LIST_MODEL } from '../tag/models';
import { setUncheckableTableTransferList, updateStoreWithPath, getRandomNumber } from '../../../lib/common';

/**
 *  INITIAL STATE
 */
const initialState = {
  /**
   *  진행중인 기획전 리스트에서 사용되는 MODEL
   */
  inProgressEvent: {
    /**
     *  진행중인 기획전 리스트
     */
    inProgressEventList: {
      /**
       *  리스트
       */
      list: [],
      /**
       *  필터 옵션
       */
      filterOption: EVENT_LIST_FILTER_OPTION_MODEL,
      /**
       *  리스트 pagination
       */
      pagination: LIST_PAGINATION_MODEL,
      /**
       *  집계 데이터
       */
      summary: LIST_SUMMARY_MODEL,
    },

    /**
     *  오리지널 진행중인 기획전 리스트
     */
    originalInProgressEventList: [],
  },

  /**
   *  전체 기획전 리스트에서 사용되는 MODEL
   */
  allEvent: {
    /**
     *  전체 기획전 리스트
     */
    allEventList: {
      /**
       *  리스트
       */
      list: [],
      /**
       *  필터 옵션
       */
      filterOption: EVENT_LIST_FILTER_OPTION_MODEL,
      /**
       *  리스트 pagination
       */
      pagination: LIST_PAGINATION_MODEL,
      /**
       *  집계 데이터
       */
      summary: LIST_SUMMARY_MODEL,
    },

    /**
     *  오리지널 전체 기획전 리스트
     */
    originalAllEventList: [],
  },

  /**
   *  기획전 등록에서 사용되는 MODEL
   */
  eventSave: {
    /**
     *  기획전 정보
     */
    eventInfo: EVENT_INFO_MODEL,

    /**
     *  기획전 섹선 정보
     */
    eventSectionInfo: {
      sectionInfoList: [{ ...EVENT_SECTION_INFO_MODEL, id: getRandomNumber() }],
      /**
       *  기획전 섹션 탭 currentTabKey
       */
      currentTabKey: 0,
      /**
       *  상품 리스트
       */
      productList: {
        /**
         * 리스트
         */
        list: [],
        /**
         *  필터 옵션
         */
        filterOption: EVENT_PRODUCT_LIST_FILTER_OPTION_MODEL,
        /**
         * pagination
         */
        pagination: _LIST_PAGINATION_MODEL,
      },
    },

    /**
     *  태그 정보
     */
    tagInfo: TAG_LINK_LIST_MODEL,

    /**
     *  탭 currentTabKey
     */
    currentTabKey: 0,

    /**
     *  기획전 아이템
     */
    eventItem: {
      /**
       *  연결될 아이템 리스트
       */
      itemList: {
        /**
         * 리스트
         */
        list: [],
        /**
         *  필터 옵션
         */
        filterOption: EVENT_PRODUCT_LIST_FILTER_OPTION_MODEL,
        /**
         * pagination
         */
        pagination: _LIST_PAGINATION_MODEL,
      },
      /**
       *  연결된 아이템 리스트
       */
      selectedItemList: {
        list: [],
        idList: [],
      },
    },

    /**
     *  저장 결과
     */
    result: EVENT_SAVE_RESULT.NONE,
  },

  /**
   *  기획전 미리보기 모달
   */
  previewEventModal: {
    isOpen: false,
    data: {},
  },
};

/**
 *  REDUCER
 */
export default handleActions(
  {
    /**
     *  [기획전] 기획전 데이터 변경하기
     */
    [actions.SET_DATA]: (state, action) => {
      return produce(state, (draft) => {
        updateStoreWithPath(action.payload.path, action.payload.value, draft);
      });
    },

    /**
     *  [진행중인 기획전] 진행중인 기획전 리스트 요청하기
     */
    [actions.GET_IN_PROGRESS_EVENT_LIST_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  진행중인 기획전 리스트
         */
        draft.inProgressEvent.inProgressEventList = action.payload.inProgressEventList;
        /**
         *  오리지널 진행중인 기획전 리스트
         */
        draft.inProgressEvent.originalInProgressEventList = action.payload.inProgressEventList.list;
      });
    },

    /**
     *  [전체 기획전] 전체 기획전 리스트 요청하기
     */
    [actions.GET_ALL_EVENT_LIST_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  전체 기획전 리스트
         */
        draft.allEvent.allEventList = action.payload.allEventList;
        draft.allEvent.originalAllEventList = action.payload.allEventList.list;
      });
    },

    /**
     *  [기획전 등록] 기획전 상품 excel 리스트 업로드하기
     */
    [actions.SET_EVENT_PRODUCT_EXCEL_UPLOAD_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        if (isNil(action.payload.eventSectionIndex)) {
          /**
           *  연결된 리스트 업데이트
           */
          draft.eventSave.eventItem.selectedItemList.list = action.payload.productList;
          draft.eventSave.eventItem.selectedItemList.idList = draft.eventSave.eventItem.selectedItemList.list.map(
            (item) => item.id,
          );

          /**
           *  업로드 아이템 중에 선택된 아이템인 경우에는
           *  아이템 선택 부분에서 이동을 할 수 없도록 하기
           */
          draft.eventSave.eventItem.itemList.list = setUncheckableTableTransferList({
            list: draft.eventSave.eventItem.itemList.list,
            selectedIdList: draft.eventSave.eventItem.selectedItemList.idList,
          });
        } else {
          /**
           *  연결된 리스트 업데이트
           */
          draft.eventSave.eventSectionInfo.sectionInfoList[
            action.payload.eventSectionIndex
          ].eventSectionItemList.selectedItemList.list = action.payload.productList;
          draft.eventSave.eventSectionInfo.sectionInfoList[
            action.payload.eventSectionIndex
          ].eventSectionItemList.selectedItemList.idList = draft.eventSave.eventSectionInfo.sectionInfoList[
            action.payload.eventSectionIndex
          ].eventSectionItemList.selectedItemList.list.map((item) => item.id);

          /**
           *  업로드 아이템 중에 선택된 아이템인 경우에는
           *  아이템 선택 부분에서 이동을 할 수 없도록 하기
           */
          draft.eventSave.eventSectionInfo.sectionInfoList[
            action.payload.eventSectionIndex
          ].eventSectionItemList.itemList.list = setUncheckableTableTransferList({
            list: draft.eventSave.eventSectionInfo.sectionInfoList[action.payload.eventSectionIndex]
              .eventSectionItemList.itemList.list,
            selectedIdList:
              draft.eventSave.eventSectionInfo.sectionInfoList[action.payload.eventSectionIndex].eventSectionItemList
                .selectedItemList.idList,
          });
        }
      });
    },

    /**
     *  모달 토글
     */
    [actions.TOGGLE_MODAL]: (state, action) => {
      return produce(state, (draft) => {
        updateStoreWithPath([action.payload.name, 'data'], action.payload.data, draft);
        updateStoreWithPath([action.payload.name, 'isOpen'], action.payload.value, draft);
      });
    },
  },
  initialState,
);
